import { useEffect, useState } from "react";
import ClientAvatar from "@/components/ClientAvatar";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Calendar, Users, DollarSign, TrendingUp, LogIn, Clock, AlertTriangle, CheckCircle, Wifi, WifiOff, ChevronLeft, ChevronRight, Plus, Ban, Bell, Instagram, CalendarClock, Sparkles, ShoppingBag } from "lucide-react";
import ActionNeededDialog from "@/components/admin/ActionNeededDialog";
import BlockedDayConflictsBar from "@/components/admin/BlockedDayConflictsBar";

import { useAvailabilityData, AvailabilityHoursEditor, BlockDateDialog, BlockedDatesList } from "@/components/AvailabilityManager";
import { format, parseISO, differenceInMinutes, formatDistanceToNow, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, addMonths, subMonths, getDay, isSameMonth } from "date-fns";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { isTestAccount, TEST_ACCOUNT_USER_IDS } from "@/lib/testAccounts";

interface TodayBooking {
  id: string;
  service_name: string;
  booking_date: string;
  booking_time: string;
  duration: string;
  price: number;
  status: string;
  user_id: string;
  checked_in_at: string | null;
}

interface AllBooking {
  id: string;
  service_name: string;
  service_category: string;
  duration: string;
  user_id: string;
  status: string;
}

const parseDurationToMinutes = (dur: string): number => {
  const lower = dur.toLowerCase().trim();
  let total = 0;
  const hrMatch = lower.match(/(\d+)\s*h/);
  const minMatch = lower.match(/(\d+)\s*m/);
  if (hrMatch) total += parseInt(hrMatch[1]) * 60;
  if (minMatch) total += parseInt(minMatch[1]);
  if (total === 0) {
    const num = parseInt(lower);
    if (!isNaN(num)) total = num;
  }
  return total;
};

const formatMinutes = (mins: number): string => {
  if (mins >= 60) {
    const h = Math.floor(mins / 60);
    const m = Math.round(mins % 60);
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  }
  return `${Math.round(mins)}m`;
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalBookings: 0,
    totalClients: 0,
    totalRevenue: 0,
    upcomingBookings: 0,
  });
  const [recentBookings, setRecentBookings] = useState<any[]>([]);
  const [todayBookings, setTodayBookings] = useState<TodayBooking[]>([]);
  const [allBookings, setAllBookings] = useState<AllBooking[]>([]);
  const [profiles, setProfiles] = useState<Record<string, string>>({});
  const [clientActivity, setClientActivity] = useState<{ user_id: string; full_name: string | null; last_sign_in_at: string | null }[]>([]);
  const [loading, setLoading] = useState(true);
  const [checkingIn, setCheckingIn] = useState<string | null>(null);
  const [timeDialogOpen, setTimeDialogOpen] = useState(false);
  const [calMonth, setCalMonth] = useState(new Date());
  const [monthBookings, setMonthBookings] = useState<{ booking_date: string; status: string }[]>([]);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [blockDialogDate, setBlockDialogDate] = useState<Date | null>(null);
  const { bookingHours, blockedDates, refetch: refetchAvailability } = useAvailabilityData();
  const [pendingCounts, setPendingCounts] = useState({ reschedules: 0, igPosts: 0, addons: 0 });
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [checkInEnabled, setCheckInEnabled] = useState(false);

  useEffect(() => {
    let active = true;
    supabase
      .from("site_settings")
      .select("value")
      .eq("key", "check_in_enabled")
      .maybeSingle()
      .then(({ data }) => {
        if (active) setCheckInEnabled(data?.value === "true");
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const fetchPending = async () => {
      // Read banner filter settings (default both ON)
      const { data: settingsRows } = await supabase
        .from("site_settings")
        .select("key, value")
        .in("key", ["banner_exclude_test_accounts", "banner_exclude_inactive_bookings"]);
      const settingsMap = new Map((settingsRows || []).map((r: any) => [r.key, r.value]));
      const excludeTest = settingsMap.get("banner_exclude_test_accounts") !== "false";
      const excludeInactive = settingsMap.get("banner_exclude_inactive_bookings") !== "false";

      const [reschedRes, igRes, addonRes] = await Promise.all([
        supabase
          .from("bookings")
          .select("id, user_id, status, booking_date", { count: "exact", head: false }).is("deleted_at", null)
          .eq("status", "pending")
          .not("reschedule_requested_at", "is", null),
        supabase
          .from("instagram_posts")
          .select("id, user_id", { count: "exact", head: false })
          .eq("status", "pending"),
        supabase
          .from("booking_addons")
          .select("id, user_id, booking_id", { count: "exact", head: false })
          .eq("status", "pending")
          .eq("requested_by", "client"),
      ]);

      // Resolve parent bookings for add-on requests so we can filter inactive ones
      const addonRows = (addonRes.data || []) as any[];
      const parentBookingIds = Array.from(
        new Set(addonRows.map((a) => a.booking_id).filter(Boolean))
      );
      let parentMap = new Map<string, { status: string; booking_date: string }>();
      if (excludeInactive && parentBookingIds.length > 0) {
        const { data: parents } = await supabase
          .from("bookings")
          .select("id, status, booking_date").is("deleted_at", null)
          .in("id", parentBookingIds);
        parentMap = new Map(
          (parents || []).map((p: any) => [p.id, { status: p.status, booking_date: p.booking_date }])
        );
      }

      const todayStr = new Date().toISOString().split("T")[0];
      const isInactiveBooking = (status?: string | null, dateStr?: string | null) =>
        status === "cancelled" || (!!dateStr && dateStr < todayStr);

      const filterTest = (rows: any[] | null | undefined) =>
        excludeTest ? (rows || []).filter((r: any) => !isTestAccount(r.user_id)) : (rows || []);

      const reschedFiltered = filterTest(reschedRes.data).filter((b: any) =>
        excludeInactive ? !isInactiveBooking(b.status, b.booking_date) : true
      );
      const addonFiltered = filterTest(addonRes.data).filter((a: any) => {
        if (!excludeInactive) return true;
        const parent = parentMap.get(a.booking_id);
        return parent ? !isInactiveBooking(parent.status, parent.booking_date) : true;
      });

      setPendingCounts({
        reschedules: reschedFiltered.length,
        igPosts: filterTest(igRes.data).length,
        addons: addonFiltered.length,
      });
    };
    fetchPending();

    const channel = supabase
      .channel("admin-pending-banner")
      .on("postgres_changes", { event: "*", schema: "public", table: "bookings" }, fetchPending)
      .on("postgres_changes", { event: "*", schema: "public", table: "instagram_posts" }, fetchPending)
      .on("postgres_changes", { event: "*", schema: "public", table: "booking_addons" }, fetchPending)
      .on("postgres_changes", { event: "*", schema: "public", table: "site_settings" }, fetchPending)
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, []);

  useEffect(() => {
    const fetchStats = async () => {
      const today = new Date().toISOString().split("T")[0];

      const [bookingsRes, clientsRes, upcomingRes, recentRes, todayRes, allBookingsRes] = await Promise.all([
        supabase.from("bookings").select("price, user_id").is("deleted_at", null),
        supabase.from("profiles").select("id, user_id"),
        supabase.from("bookings").select("id, user_id").is("deleted_at", null).gte("booking_date", today).neq("status", "cancelled"),
        supabase.from("bookings").select("*").is("deleted_at", null).order("created_at", { ascending: false }).limit(20),
        supabase.from("bookings").select("*").is("deleted_at", null).eq("booking_date", today).neq("status", "cancelled").order("booking_time", { ascending: true }),
        supabase.from("bookings").select("id, service_name, service_category, duration, user_id, status").is("deleted_at", null).neq("status", "cancelled"),
      ]);

      const allBookingsForRevenue = (bookingsRes.data || []).filter((b: any) => !isTestAccount(b.user_id));
      const totalRevenue = allBookingsForRevenue.reduce((sum, b) => sum + Number(b.price), 0);
      const upcomingFiltered = (upcomingRes.data || []).filter((b: any) => !isTestAccount(b.user_id));
      const realClients = (clientsRes.data || []).filter((p: any) => !isTestAccount(p.user_id));

      setStats({
        totalBookings: allBookingsForRevenue.length,
        totalClients: realClients.length,
        totalRevenue,
        upcomingBookings: upcomingFiltered.length,
      });
      const filterT = (rows: any[] | null | undefined) => (rows || []).filter((b: any) => !isTestAccount(b.user_id));
      setRecentBookings(filterT(recentRes.data).slice(0, 5));
      setTodayBookings(filterT(todayRes.data) as TodayBooking[]);
      setAllBookings(filterT(allBookingsRes.data) as AllBooking[]);

      // Fetch profiles for today's bookings + all bookings
      const allBkgs = [...(recentRes.data || []), ...(todayRes.data || []), ...(allBookingsRes.data || [])];
      const userIds = [...new Set(allBkgs.map((b: any) => b.user_id))];
      if (userIds.length > 0) {
        const { data: profileData } = await supabase
          .from("profiles")
          .select("user_id, full_name")
          .in("user_id", userIds);
        const map: Record<string, string> = {};
        (profileData || []).forEach((p) => { map[p.user_id] = p.full_name || "Unknown"; });
        setProfiles(map);
      }

      // Fetch client activity (recent sign-ins)
      const excludeIds = Array.from(TEST_ACCOUNT_USER_IDS);
      let activityQuery = supabase
        .from("profiles")
        .select("user_id, full_name, last_sign_in_at")
        .not("last_sign_in_at", "is", null);
      if (excludeIds.length > 0) {
        activityQuery = activityQuery.not("user_id", "in", `(${excludeIds.join(",")})`);
      }
      const { data: activityData } = await activityQuery
        .order("last_sign_in_at", { ascending: false })
        .limit(20);
      setClientActivity((activityData as any[]) || []);

      setLoading(false);
    };

    fetchStats();
  }, []);

  // Fetch bookings for the availability calendar month
  useEffect(() => {
    const fetchMonthBookings = async () => {
      const mStart = format(startOfMonth(calMonth), "yyyy-MM-dd");
      const mEnd = format(endOfMonth(calMonth), "yyyy-MM-dd");
      const { data } = await supabase
        .from("bookings")
        .select("booking_date, status, user_id").is("deleted_at", null)
        .gte("booking_date", mStart)
        .lte("booking_date", mEnd);
      setMonthBookings((data || []).filter((b: any) => !isTestAccount(b.user_id)));
    };
    fetchMonthBookings();
  }, [calMonth]);

  // Calendar helpers
  const calMonthStart = startOfMonth(calMonth);
  const calMonthEnd = endOfMonth(calMonth);
  const calDays = eachDayOfInterval({ start: calMonthStart, end: calMonthEnd });
  const startPadding = getDay(calMonthStart);
  const bookingsByDate: Record<string, number> = {};
  monthBookings.filter(b => b.status !== "cancelled").forEach(b => {
    bookingsByDate[b.booking_date] = (bookingsByDate[b.booking_date] || 0) + 1;
  });
  const blockedDateSet = new Set(blockedDates.map(b => b.blocked_date));

  const handleQuickCheckIn = async (booking: TodayBooking) => {
    setCheckingIn(booking.id);
    const now = new Date().toISOString();
    await supabase.from("bookings").update({ checked_in_at: now }).eq("id", booking.id);
    setTodayBookings((prev) =>
      prev.map((b) => (b.id === booking.id ? { ...b, checked_in_at: now } : b))
    );
    setCheckingIn(null);
  };

  const getArrivalStatus = (booking: TodayBooking) => {
    if (!booking.checked_in_at) return null;

    const today = booking.booking_date;
    const timeParts = booking.booking_time.match(/(\d+):(\d+)\s*(AM|PM)/i);
    if (!timeParts) return null;

    let hours = parseInt(timeParts[1]);
    const minutes = parseInt(timeParts[2]);
    const ampm = timeParts[3].toUpperCase();
    if (ampm === "PM" && hours !== 12) hours += 12;
    if (ampm === "AM" && hours === 12) hours = 0;

    const scheduledTime = new Date(`${today}T${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:00`);
    const arrivalTime = new Date(booking.checked_in_at);
    const diff = differenceInMinutes(arrivalTime, scheduledTime);

    if (diff > 5) return { late: true, minutes: diff };
    if (diff < -5) return { late: false, minutes: Math.abs(diff), early: true };
    return { late: false, minutes: 0 };
  };

  // Compute avg appointment duration
  const durations = allBookings.map(b => parseDurationToMinutes(b.duration)).filter(d => d > 0);
  const avgDuration = durations.length > 0 ? durations.reduce((a, b) => a + b, 0) / durations.length : 0;

  // Breakdown by service
  const byService: Record<string, { total: number; count: number }> = {};
  allBookings.forEach(b => {
    const mins = parseDurationToMinutes(b.duration);
    if (mins <= 0) return;
    if (!byService[b.service_name]) byService[b.service_name] = { total: 0, count: 0 };
    byService[b.service_name].total += mins;
    byService[b.service_name].count += 1;
  });
  const serviceBreakdown = Object.entries(byService)
    .map(([name, { total, count }]) => ({ name, avg: total / count, count }))
    .sort((a, b) => b.count - a.count);

  // Breakdown by client
  const byClient: Record<string, { total: number; count: number }> = {};
  allBookings.forEach(b => {
    const mins = parseDurationToMinutes(b.duration);
    if (mins <= 0) return;
    if (!byClient[b.user_id]) byClient[b.user_id] = { total: 0, count: 0 };
    byClient[b.user_id].total += mins;
    byClient[b.user_id].count += 1;
  });
  const clientBreakdown = Object.entries(byClient)
    .map(([userId, { total, count }]) => ({ userId, name: profiles[userId] || "Unknown", avg: total / count, count, totalMins: total }))
    .sort((a, b) => b.count - a.count);

  const statCards = [
    { label: "Total Bookings", value: stats.totalBookings, icon: Calendar, format: (v: number) => String(v), path: "/admin/bookings" },
    { label: "Total Clients", value: stats.totalClients, icon: Users, format: (v: number) => String(v), path: "/admin/clients" },
    { label: "Total Revenue", value: stats.totalRevenue, icon: DollarSign, format: (v: number) => `$${v.toLocaleString()}`, path: "/admin/revenue" },
    { label: "Upcoming", value: stats.upcomingBookings, icon: TrendingUp, format: (v: number) => String(v), path: "/admin/calendar" },
  ];

  if (loading) {
    return <div className="text-muted-foreground text-sm">Loading dashboard…</div>;
  }

  return (
    <div className="space-y-8">
      <BlockedDayConflictsBar />

      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-serif text-3xl font-bold italic">Dashboard</h1>
          <p className="text-muted-foreground text-sm mt-1">Overview of your business</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={() => navigate("/admin/settings#checkout")}
            className="rounded-none px-6 py-5 text-xs font-medium tracking-[0.3em] uppercase border-primary/60 hover:bg-primary/10 hover:text-foreground"
          >
            <ShoppingBag size={14} className="mr-2" /> Checkout
          </Button>
          <Button onClick={() => navigate("/book")} className="rounded-none px-8 py-5 text-xs font-medium tracking-[0.3em] uppercase border border-primary hover:bg-primary/90">
            <Calendar size={14} className="mr-2" /> Book an Appointment
          </Button>
        </div>
      </div>

      {/* Pending action banner */}
      {(pendingCounts.reschedules + pendingCounts.igPosts + pendingCounts.addons) > 0 && (
        <button
          type="button"
          onClick={() => setActionDialogOpen(true)}
          className="w-full text-left animate-slow-flash rounded-md border border-primary/60 bg-primary/10 p-4 shadow-[0_0_24px_-6px_hsl(var(--primary)/0.6)] hover:bg-primary/15 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          aria-label="Open action needed summary"
        >
          <div className="flex items-start gap-3 flex-wrap">
            <Bell size={18} className="text-primary mt-0.5 shrink-0" />
            <div className="flex-1 min-w-[200px]">
              <p className="text-sm font-medium tracking-wide uppercase text-primary">
                Action needed
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Tap to review and approve items waiting for you.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {pendingCounts.reschedules > 0 && (
                <Badge variant="outline" className="text-[11px] gap-1.5 border-primary/40 px-2 py-1">
                  <CalendarClock size={12} />
                  {pendingCounts.reschedules} Reschedule{pendingCounts.reschedules > 1 ? "s" : ""}
                </Badge>
              )}
              {pendingCounts.igPosts > 0 && (
                <Badge variant="outline" className="text-[11px] gap-1.5 border-primary/40 px-2 py-1">
                  <Instagram size={12} />
                  {pendingCounts.igPosts} IG Credit{pendingCounts.igPosts > 1 ? "s" : ""}
                </Badge>
              )}
              {pendingCounts.addons > 0 && (
                <Badge variant="outline" className="text-[11px] gap-1.5 border-primary/40 px-2 py-1">
                  <Sparkles size={12} />
                  {pendingCounts.addons} Add-on{pendingCounts.addons > 1 ? "s" : ""}
                </Badge>
              )}
              <ChevronRight size={16} className="text-primary" />
            </div>
          </div>
        </button>
      )}

      <ActionNeededDialog
        open={actionDialogOpen}
        onOpenChange={setActionDialogOpen}
      />

      {/* Availability Calendar */}
      <Card className="bg-card border-border/50">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-4">
            <Button variant="ghost" size="icon" onClick={() => setCalMonth(m => subMonths(m, 1))}>
              <ChevronLeft size={16} />
            </Button>
            <h3 className="text-sm font-medium tracking-[0.2em] uppercase">
              {format(calMonth, "MMMM yyyy")}
            </h3>
            <Button variant="ghost" size="icon" onClick={() => setCalMonth(m => addMonths(m, 1))}>
              <ChevronRight size={16} />
            </Button>
          </div>

          {/* Availability action buttons */}
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <AvailabilityHoursEditor bookingHours={bookingHours} onSave={refetchAvailability} />
            <Button
              variant="outline"
              size="sm"
              className="text-xs gap-1.5"
              onClick={() => { setBlockDialogDate(null); setBlockDialogOpen(true); }}
            >
              <Plus size={13} /> Block Date
            </Button>
            <BlockedDatesList blockedDates={blockedDates} onDelete={refetchAvailability} />
          </div>

          <div className="grid grid-cols-7 gap-1">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(d => (
              <div key={d} className="text-center text-[10px] font-medium text-muted-foreground tracking-widest uppercase py-1">{d}</div>
            ))}
            {Array.from({ length: startPadding }).map((_, i) => (
              <div key={`pad-${i}`} />
            ))}
            {calDays.map(day => {
              const dateStr = format(day, "yyyy-MM-dd");
              const count = bookingsByDate[dateStr] || 0;
              const isToday = isSameDay(day, new Date());
              const isPast = day < new Date() && !isToday;
              const isBlocked = blockedDateSet.has(dateStr);
              const dayOfWeek = getDay(day);
              const dayHours = bookingHours.find(h => h.day_of_week === dayOfWeek);
              const isClosed = dayHours && !dayHours.is_available;
              return (
                <button
                  key={dateStr}
                  onClick={() => {
                    navigate(`/admin/bookings?date=${dateStr}`);
                  }}
                  className={`relative flex flex-col items-center justify-center py-2 rounded-md text-xs transition-all hover:bg-accent/50 overflow-hidden ${
                    isToday ? "ring-1 ring-primary bg-primary/10 font-bold" : isPast ? "text-muted-foreground/50" : ""
                  } ${isBlocked ? "bg-destructive/10 ring-1 ring-destructive/40 line-through text-destructive" : ""} ${isClosed && !isBlocked ? "bg-muted/50" : ""}`}
                  style={
                    isBlocked
                      ? {
                          backgroundImage:
                            "repeating-linear-gradient(45deg, hsl(var(--destructive) / 0.25) 0, hsl(var(--destructive) / 0.25) 2px, transparent 2px, transparent 8px)",
                        }
                      : undefined
                  }
                  aria-label={isBlocked ? `${format(day, "MMM d")} blocked` : format(day, "MMM d")}
                >
                  <span className="relative z-10">{format(day, "d")}</span>
                  {isBlocked ? (
                    <Ban size={10} className="mt-0.5 text-destructive relative z-10" />
                  ) : isClosed ? (
                    <span className="mt-0.5 text-[8px] text-muted-foreground">off</span>
                  ) : count > 0 ? (
                    <span className={`mt-0.5 text-[9px] font-semibold px-1.5 rounded-full ${
                      count >= 5 ? "bg-destructive/20 text-destructive" : count >= 3 ? "bg-primary/20 text-primary" : "bg-muted text-muted-foreground"
                    }`}>
                      {count}
                    </span>
                  ) : !isPast ? (
                    <span className="mt-0.5 w-1.5 h-1.5 rounded-full bg-green-500/40" />
                  ) : null}
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-4 mt-3 text-[10px] text-muted-foreground tracking-widest uppercase flex-wrap">
            <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-green-500/40" /> Open</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-muted text-muted-foreground text-[8px] flex items-center justify-center font-bold">2</span> Light</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-primary/20 text-primary text-[8px] flex items-center justify-center font-bold">3</span> Moderate</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-destructive/20 text-destructive text-[8px] flex items-center justify-center font-bold">5</span> Busy</span>
            <span className="flex items-center gap-1"><Ban size={10} className="text-destructive" /> Blocked</span>
            <span className="flex items-center gap-1 text-muted-foreground">off = Closed</span>
          </div>
        </CardContent>
      </Card>

      <BlockDateDialog
        selectedDate={blockDialogDate}
        onSave={() => { refetchAvailability(); }}
        open={blockDialogOpen}
        onOpenChange={setBlockDialogOpen}
      />

      <Card className="bg-card border-border/50">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-light tracking-[0.3em] uppercase text-muted-foreground flex items-center gap-2">
            <LogIn size={16} className="text-primary" />
            Today's Appointments — {format(new Date(), "EEEE, MMMM d")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {todayBookings.length === 0 ? (
            <p className="text-sm text-muted-foreground">No appointments today.</p>
          ) : (
            <div className="space-y-2">
              {todayBookings.map((b) => {
                const arrival = getArrivalStatus(b);
                return (
                  <div
                    key={b.id}
                    className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                      checkInEnabled && b.checked_in_at
                        ? arrival?.late
                          ? "border-destructive/30 bg-destructive/5"
                          : "border-green-500/30 bg-green-500/5"
                        : "border-border/30 bg-background"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="text-center min-w-[60px]">
                        <p className="text-sm font-bold">{b.booking_time}</p>
                      </div>
                      <ClientAvatar userId={b.user_id} name={profiles[b.user_id]} size="sm" />
                      <div>
                        <p className="text-sm font-medium">
                          {profiles[b.user_id] || "Unknown Client"}
                        </p>
                        <p className="text-xs text-muted-foreground">{b.service_name} · {b.duration}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {checkInEnabled && (b.checked_in_at ? (
                        <div className="flex items-center gap-2">
                          {arrival?.late ? (
                            <Badge variant="destructive" className="text-[10px] gap-1">
                              <AlertTriangle size={10} />
                              {arrival.minutes}min late
                            </Badge>
                          ) : arrival?.early ? (
                            <Badge variant="secondary" className="text-[10px] gap-1">
                              <Clock size={10} />
                              {arrival.minutes}min early
                            </Badge>
                          ) : (
                            <Badge variant="default" className="text-[10px] gap-1">
                              <LogIn size={10} />
                              On time
                            </Badge>
                          )}
                          <span className="text-[10px] text-muted-foreground">
                            {format(new Date(b.checked_in_at), "h:mm a")}
                          </span>
                        </div>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs gap-1 border-green-500/30 text-green-700 hover:bg-green-500/10"
                          disabled={checkingIn === b.id}
                          onClick={() => handleQuickCheckIn(b)}
                        >
                          <LogIn size={12} />
                          {checkingIn === b.id ? "…" : "Check In"}
                        </Button>
                      ))}
                      {b.status === "confirmed" && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs gap-1"
                          onClick={() => navigate(`/admin/bookings?complete=${b.id}`)}
                        >
                          <CheckCircle size={12} /> Complete
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {statCards.map((s) => (
          <Card
            key={s.label}
            className="bg-card border-border/50 cursor-pointer hover:border-primary/50 hover:shadow-md transition-all"
            onClick={() => navigate(s.path)}
          >
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-xs font-light tracking-[0.3em] uppercase text-muted-foreground">
                {s.label}
              </CardTitle>
              <s.icon size={16} className="text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{s.format(s.value)}</div>
              <p className="text-[10px] text-muted-foreground mt-1 tracking-widest uppercase">Click to view →</p>
            </CardContent>
          </Card>
        ))}

        {/* Avg Appointment Time Card */}
        <Card
          className="bg-card border-border/50 cursor-pointer hover:border-primary/50 hover:shadow-md transition-all"
          onClick={() => setTimeDialogOpen(true)}
        >
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs font-light tracking-[0.3em] uppercase text-muted-foreground">
              Avg Appt Time
            </CardTitle>
            <Clock size={16} className="text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{avgDuration > 0 ? formatMinutes(avgDuration) : "N/A"}</div>
            <p className="text-[10px] text-muted-foreground mt-1 tracking-widest uppercase">Click for breakdown →</p>
          </CardContent>
        </Card>
      </div>

      {/* Time Breakdown Dialog */}
      <Dialog open={timeDialogOpen} onOpenChange={setTimeDialogOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock size={18} /> Appointment Time Breakdown
            </DialogTitle>
          </DialogHeader>
          <Tabs defaultValue="service">
            <TabsList className="w-full">
              <TabsTrigger value="service" className="flex-1">By Service</TabsTrigger>
              <TabsTrigger value="client" className="flex-1">By Client</TabsTrigger>
            </TabsList>
            <TabsContent value="service" className="space-y-2 mt-4">
              {serviceBreakdown.length === 0 ? (
                <p className="text-sm text-muted-foreground">No data yet.</p>
              ) : (
                serviceBreakdown.map((s) => (
                  <div key={s.name} className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border border-border/30">
                    <div>
                      <p className="text-sm font-medium">{s.name}</p>
                      <p className="text-xs text-muted-foreground">{s.count} appointment{s.count !== 1 ? "s" : ""}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold">{formatMinutes(s.avg)}</p>
                      <p className="text-[10px] text-muted-foreground">avg duration</p>
                    </div>
                  </div>
                ))
              )}
            </TabsContent>
            <TabsContent value="client" className="space-y-2 mt-4">
              {clientBreakdown.length === 0 ? (
                <p className="text-sm text-muted-foreground">No data yet.</p>
              ) : (
                clientBreakdown.map((c) => (
                  <div
                    key={c.userId}
                    className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border border-border/30 cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => { setTimeDialogOpen(false); navigate(`/admin/clients/${c.userId}`); }}
                  >
                    <div>
                      <p className="text-sm font-medium">{c.name}</p>
                      <p className="text-xs text-muted-foreground">{c.count} appt{c.count !== 1 ? "s" : ""} · {formatMinutes(c.totalMins)} total</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold">{formatMinutes(c.avg)}</p>
                      <p className="text-[10px] text-muted-foreground">avg duration</p>
                    </div>
                  </div>
                ))
              )}
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      <Card className="bg-card border-border/50">
        <CardHeader>
          <CardTitle className="text-sm font-light tracking-[0.3em] uppercase text-muted-foreground">
            Recent Bookings
          </CardTitle>
        </CardHeader>
        <CardContent>
          {recentBookings.length === 0 ? (
            <p className="text-muted-foreground text-sm">No bookings yet.</p>
          ) : (
            <div className="space-y-3">
              {recentBookings.map((b) => (
                <div key={b.id} className="flex justify-between items-center border-b border-border/30 pb-3 last:border-0">
                  <div className="flex items-center gap-3">
                    <ClientAvatar userId={b.user_id} name={profiles[b.user_id]} size="sm" />
                    <div>
                      <p className="text-sm font-medium">{b.service_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {profiles[b.user_id] || ""} · {b.booking_date} at {b.booking_time}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium">${Number(b.price).toFixed(0)}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      b.status === "confirmed" ? "bg-primary/20 text-primary" :
                      b.status === "cancelled" ? "bg-destructive/20 text-destructive" :
                      "bg-muted text-muted-foreground"
                    }`}>
                      {b.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Client Activity */}
      {(() => {
        const now = new Date();
        const onlineThreshold = 5 * 60 * 1000;
        const onlineClients = clientActivity.filter(
          (c) => c.last_sign_in_at && now.getTime() - new Date(c.last_sign_in_at).getTime() < onlineThreshold
        );
        const recentSignIns = clientActivity.slice(0, 10);

        return (
          <Card className="bg-card border-border/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-light tracking-[0.3em] uppercase text-muted-foreground flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Wifi size={16} className="text-primary" />
                  Client Activity
                </span>
                {onlineClients.length > 0 && (
                  <Badge variant="default" className="text-[10px] gap-1 animate-pulse">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary-foreground inline-block" />
                    {onlineClients.length} online now
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {recentSignIns.length === 0 ? (
                <p className="text-sm text-muted-foreground">No sign-in activity yet.</p>
              ) : (
                <div className="space-y-2">
                  {recentSignIns.map((c) => {
                    const isOnline = c.last_sign_in_at && now.getTime() - new Date(c.last_sign_in_at).getTime() < onlineThreshold;
                    return (
                      <div
                        key={c.user_id}
                        className={`flex items-center justify-between p-3 rounded-lg border transition-all cursor-pointer hover:bg-muted/30 ${
                          isOnline ? "border-primary/30 bg-primary/5" : "border-border/30 bg-background"
                        }`}
                        onClick={() => navigate(`/admin/clients/${c.user_id}`)}
                      >
                        <div className="flex items-center gap-3">
                          <div className="relative">
                            <ClientAvatar userId={c.user_id} name={c.full_name} size="sm" />
                            <div className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-card ${isOnline ? "bg-green-500 animate-pulse" : "bg-muted-foreground/30"}`} />
                          </div>
                          <div>
                            <p className="text-sm font-medium">{c.full_name || "Unknown Client"}</p>
                            <p className="text-xs text-muted-foreground">
                              {c.last_sign_in_at
                                ? isOnline
                                  ? "Online now"
                                  : `Last seen ${formatDistanceToNow(new Date(c.last_sign_in_at), { addSuffix: true })}`
                                : "Never signed in"}
                            </p>
                          </div>
                        </div>
                        {isOnline ? (
                          <Badge variant="secondary" className="text-[10px] gap-1 border-primary/20">
                            <Wifi size={10} /> Online
                          </Badge>
                        ) : (
                          <span className="text-[10px] text-muted-foreground tracking-widest uppercase">
                            {c.last_sign_in_at ? format(new Date(c.last_sign_in_at), "MMM d, h:mm a") : ""}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })()}
    </div>
  );
};

export default Dashboard;
